/**
 * Fire-and-forget marketing-site telemetry beacon.
 *
 * Mirrors the SPA's src/web/src/modules/telemetry/log-event.ts but with no
 * shared apiFetch dependency — the marketing bundle is a standalone build.
 * Posts to /api/web-events on the same edge server, where it lands as a typed
 * `web.action` row in ori_agent_business_events (queryable by the admin
 * dashboard), unlike posthog autocapture which only reaches ori_web_ui_events.
 *
 * Event names: dotted snake_case (e.g. `marketing.message_ori_click`).
 * Properties: flat strings/numbers/booleans/null. Errors are swallowed so a
 * failed beacon never breaks the user-facing flow.
 */

import posthog from 'posthog-js';

export type LogEventProperties = Record<string, string | number | boolean | null | undefined>;

const EVENT_NAME_RE = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;

function clean(props?: LogEventProperties): Record<string, string | number | boolean | null> {
  if (!props || typeof props !== 'object') return {};
  const out: Record<string, string | number | boolean | null> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined) continue;
    out[k] = v as string | number | boolean | null;
  }
  return out;
}

// Forward-stitch: stamp the PostHog client ids onto every web-events beacon so
// the admin user-journey panel can link this anonymous browser session
// (ori_web_ui_events, keyed by distinct_id) to the real actor once they
// onboard. Best-effort and fully guarded — never break the beacon.
function withTrackingIds(
  props: Record<string, string | number | boolean | null>,
): Record<string, string | number | boolean | null> {
  try {
    if (typeof (posthog as { get_distinct_id?: unknown }).get_distinct_id !== 'function') return props;
    const distinctId = posthog.get_distinct_id?.();
    if (distinctId && props.distinct_id == null) props.distinct_id = String(distinctId);
    const sessionId = posthog.get_session_id?.();
    if (sessionId && props.session_id == null) props.session_id = String(sessionId);
  } catch {
    // posthog not ready / disabled — skip stitching ids silently.
  }
  return props;
}

export function logEvent(event: string, properties?: LogEventProperties): void {
  const name = String(event || '').trim().toLowerCase();
  if (!name || !EVENT_NAME_RE.test(name)) {
    if (typeof console !== 'undefined') {
      console.warn('logEvent: invalid event name', event);
    }
    return;
  }
  if (typeof fetch === 'undefined') return;
  void fetch('/api/web-events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event: name, properties: withTrackingIds(clean(properties)) }),
    keepalive: true,
  }).catch(() => {});
}
